import { atom } from "jotai";

import { HoleCount } from "../components/TimeSlotList/components/__generated__/TimeSlotListInternalQuery.graphql";
import { useAgendaTeeSheetSlotsChangedSubscription$data } from "../components/TimeSlotList/hooks/__generated__/useAgendaTeeSheetSlotsChangedSubscription.graphql";
import { TeeSheetFilterType } from "../types";

export const teeSheetAgendaLastUpdatedAtom =
  atom<useAgendaTeeSheetSlotsChangedSubscription$data | null>(null);

export const maxSelectedTeeTimesAtom = atom<number>(0);

export const selectedTeeTimesAtom = atom<string[]>([]);

/* sidebar filter atoms */
export const teeSheetListFilterAtom = atom<TeeSheetFilterType>("All");

export const teeSheetGroupSizeFilterAtom = atom<number>(1);

export const teeSheetHoleFilterAtom = atom<HoleCount | null>(null); //null means any

export const teeSheetRequestBookingInfoAtom = atom<{
  queueSlotId: string | null;
  requestedTime: string | null;
} | null>(null);

export const teeSheetTimesAtom = atom<
  { timeGTE?: string; timeLTE?: string } | undefined
>(undefined); //undefined means from earliest to latest

export const isBookingMultipleTeeTimesAtom = atom<boolean>(false);

/**
 * Exits the "book multiple tee times" mode and clears the pending tee-time
 * selection.
 *
 * The tee sheet's broader `useResetTeeSheetFilters` only runs on unmount, but
 * React Navigation keeps the tee sheet mounted under the booking summary in
 * the navigation stack. Without an explicit exit call at the end of a
 * multi-booking flow, users return to the tee sheet still in "book multiple"
 * mode, which hides the just-confirmed bookings behind the
 * `isBookingMultipleTeeTimes` UI branches in `PrivateTimeSlotCardWrapper` and
 * `TimeSlotRow`.
 *
 * Call this from any flow that completes (or cleanly aborts) a multi-booking
 * sequence — e.g. after `confirmDraftBookings` succeeds.
 */
export const exitMultiTeeTimeModeAtom = atom(null, (_get, set) => {
  set(isBookingMultipleTeeTimesAtom, false);
  set(selectedTeeTimesAtom, []);
});

/* sidebar filter set atom */
export const setTeeSheetFiltersAtom = atom(
  null,
  (
    get,
    set,
    values: {
      listFilter: TeeSheetFilterType;
      groupSizeFilter: number;
      holeFilter: HoleCount | null;
      times: { timeGTE?: string; timeLTE?: string } | undefined;
      isBookingMultipleTeeTimes: boolean;
    }
  ) => {
    set(teeSheetListFilterAtom, values.listFilter);
    set(teeSheetGroupSizeFilterAtom, values.groupSizeFilter);
    set(teeSheetHoleFilterAtom, values.holeFilter);
    set(teeSheetTimesAtom, values.times);
    set(isBookingMultipleTeeTimesAtom, values.isBookingMultipleTeeTimes);
  }
);
